import React, { useState } from "react";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import { Box, Popover, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { getLogisticsProgressData } from "../../utils/logisticsProgress";
import { isTrackedLogisticsAsset } from "../../models/logisticsProgressPayload";
import AssetLogisticsStatusCard from "./AssetLogisticsStatusCard";

const ACTIVE_GREY = "#757575";
const COMPLETED_CONNECTOR_GREY = "#9E9E9E";
const PENDING_GREY = "#BDBDBD";
const PENDING_CONNECTOR_GREY = "#D8DDE3";

const DENSITY_STYLES = {
  default: {
    nodeSize: 22,
    connectorHeight: 4,
    labelFontSize: 10,
    labelGap: 0.75,
  },
  compact: {
    nodeSize: 16,
    connectorHeight: 3,
    labelFontSize: 9,
    labelGap: 0.5,
  },
};

const AssetCardProgressTracker = ({
  item,
  density = "default",
  sx,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const progress = getLogisticsProgressData(item);
  const styles = DENSITY_STYLES[density] || DENSITY_STYLES.default;
  const isTracked = isTrackedLogisticsAsset(item);
  const trackerLabel = progress.displayName
    ? `${progress.displayName} logistics progress`
    : "Logistics progress";
  const popoverOpen = Boolean(anchorEl);
  const popoverId = popoverOpen ? "asset-logistics-status-popover" : undefined;

  const handleOpenPopover = (event) => {
    if (!isTracked) return;
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
  };

  const stopPropagation = (event) => {
    event.stopPropagation();
  };

  const handleBackdropClick = (event) => {
    event.stopPropagation();
    handleClosePopover();
  };

  const handleStatusCardClose = (event) => {
    event.stopPropagation();
    handleClosePopover();
  };

  const handleKeyDown = (event) => {
    if (event.key !== "Enter" && event.key !== " ") return;

    event.preventDefault();
    handleOpenPopover(event);
  };

  return (
    <>
      <Box
        role={isTracked ? "button" : undefined}
        aria-hidden={isTracked ? undefined : true}
        aria-label={isTracked ? `${trackerLabel} details` : undefined}
        aria-controls={isTracked ? popoverId : undefined}
        aria-haspopup={isTracked ? "dialog" : undefined}
        aria-expanded={isTracked && popoverOpen ? "true" : undefined}
        tabIndex={isTracked ? 0 : -1}
        onClick={isTracked ? handleOpenPopover : undefined}
        onKeyDown={isTracked ? handleKeyDown : undefined}
        onMouseDown={isTracked ? stopPropagation : undefined}
        onPointerDown={isTracked ? stopPropagation : undefined}
        sx={{
          position: "relative",
          width: "100%",
          minWidth: 0,
          borderRadius: 1.5,
          // Untracked items (Equipment) still render the exact same tracker
          // markup below so their card takes up identical space — just
          // invisible — instead of reserving a guessed-at height that can
          // drift from the real one.
          visibility: isTracked ? "visible" : "hidden",
          pointerEvents: isTracked ? "auto" : "none",
          cursor: isTracked ? "pointer" : "default",
          outline: "none",
          transition:
            "background-color 160ms ease, box-shadow 160ms ease, filter 160ms ease",
          "&:hover": {
            backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.06),
            boxShadow: (theme) =>
              `0 0 0 1px ${alpha(theme.palette.primary.main, 0.16)}`,
          },
          "&:focus-visible": {
            backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08),
            boxShadow: (theme) =>
              `0 0 0 2px ${alpha(theme.palette.primary.main, 0.32)}`,
          },
          ...(popoverOpen && {
            backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08),
            boxShadow: (theme) =>
              `0 0 0 1px ${alpha(theme.palette.primary.main, 0.22)}`,
          }),
          ...sx,
        }}
      >
        <Box
          role="list"
          aria-label={trackerLabel}
          data-density={density}
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
            width: "100%",
            minWidth: 0,
            overflow: "hidden",
            visibility: isTracked ? "visible" : "hidden",
          }}
        >
          {progress.stages.map((stage, index) => {
            const isCompleted = stage.status === "completed" || stage.isFinalMilestoneReached;
            const isCurrent = stage.status === "in-progress" && !stage.isFinalMilestoneReached;
            const isActive = isCompleted || isCurrent;
            const nextStage = progress.stages[index + 1];
            const connectorColor =
              nextStage && nextStage.status !== "pending"
                ? COMPLETED_CONNECTOR_GREY
                : PENDING_CONNECTOR_GREY;

            return (
              <Box
                key={stage.key}
                role="listitem"
                aria-label={`${stage.title}: ${stage.statusLabel}`}
                data-status={stage.status}
                sx={{
                  minWidth: 0,
                  position: "relative",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: styles.labelGap,
                }}
              >
                {index < progress.stages.length - 1 && (
                  <Box
                    aria-hidden="true"
                    sx={{
                      position: "absolute",
                      zIndex: 0,
                      top: (styles.nodeSize - styles.connectorHeight) / 2,
                      left: "50%",
                      width: "100%",
                      height: styles.connectorHeight,
                      backgroundColor: connectorColor,
                    }}
                  />
                )}

                <Box
                  aria-hidden="true"
                  sx={{
                    position: "relative",
                    zIndex: 1,
                    width: styles.nodeSize,
                    height: styles.nodeSize,
                    flexShrink: 0,
                    borderRadius: "50%",
                    border: "2px solid",
                    borderColor: isActive ? ACTIVE_GREY : PENDING_GREY,
                    backgroundColor: isCompleted ? ACTIVE_GREY : "#fff",
                    color: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: isCurrent
                      ? "0 0 0 3px rgba(117, 117, 117, 0.14)"
                      : "none",
                  }}
                >
                  {isCompleted && (
                    <CheckRoundedIcon
                      sx={{ fontSize: Math.round(styles.nodeSize * 0.68) }}
                    />
                  )}
                  {isCurrent && (
                    <Box
                      sx={{
                        width: Math.max(6, Math.round(styles.nodeSize * 0.4)),
                        height: Math.max(6, Math.round(styles.nodeSize * 0.4)),
                        borderRadius: "50%",
                        backgroundColor: ACTIVE_GREY,
                      }}
                    />
                  )}
                </Box>

                <Typography
                  aria-hidden="true"
                  sx={{
                    maxWidth: "100%",
                    minWidth: 0,
                    fontSize: styles.labelFontSize,
                    fontWeight: 800,
                    lineHeight: 1.1,
                    textAlign: "center",
                    color: isActive ? "text.primary" : "text.secondary",
                    whiteSpace: "nowrap",
                  }}
                >
                  {stage.cardLabel}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>

      <Popover
        id={popoverId}
        open={popoverOpen}
        anchorEl={anchorEl}
        onClose={handleClosePopover}
        onClick={stopPropagation}
        onMouseDown={stopPropagation}
        onPointerDown={stopPropagation}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        PaperProps={{
          onClick: stopPropagation,
          onMouseDown: stopPropagation,
          onPointerDown: stopPropagation,
          sx: {
            mt: 0.75,
            borderRadius: 2,
            boxShadow: "0 18px 42px rgba(15, 23, 42, 0.18)",
          },
        }}
        BackdropProps={{
          "data-testid": "asset-logistics-status-backdrop",
          onClick: handleBackdropClick,
          onMouseDown: stopPropagation,
          onPointerDown: stopPropagation,
        }}
      >
        <AssetLogisticsStatusCard item={item} onClose={handleStatusCardClose} />
      </Popover>
    </>
  );
};

export default AssetCardProgressTracker;
